<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';
import { useI18n } from 'vue-i18n';
import { Button } from '@/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu';
import { ChevronDownIcon, UploadIcon } from '@lucide/vue';

const props = defineProps<{
  /** The order export URL, carrying the filters currently applied to the listing. */
  exportUrl: string,
}>();

const { t } = useI18n();
const running = ref(false);

/**
 * The item export answers the same filters from the sibling endpoint, so its URL is this
 * one with the resource swapped. Only the path is rewritten; the query is left alone.
 */
const orderItemsExportUrl = computed(() => {
  if (!props.exportUrl) {
    return '';
  }

  const url = new URL(props.exportUrl, window.location.origin);
  url.pathname = url.pathname.replace(/\/orders$/, '/order-items');

  return url.toString();
});

/**
 * An export is built on a queue and mailed, so there is nothing to download here: the
 * request only says it started.
 */
async function start(url: string) {
  if (running.value || !url) {
    return;
  }

  running.value = true;

  try {
    const { data } = await axios.get(url);
    toast.success(data.message);
  } catch (error) {
    toast.error(t('The export could not be started.'));
  } finally {
    running.value = false;
  }
}

// The dropdown items are teleported out of the component, so a test drives it from here.
defineExpose({ orderItemsExportUrl, start });
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="outline" :disabled="running || !exportUrl">
        <UploadIcon class="tw:w-3 tw:h-3" />
        {{ $t('Export to Excel format') }}
        <ChevronDownIcon class="tw:w-3 tw:h-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem @click="start(props.exportUrl)">
        {{ $t('Export the orders') }}
      </DropdownMenuItem>
      <DropdownMenuItem @click="start(orderItemsExportUrl)">
        {{ $t('Export the order items') }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
